/**
 * `<value>.entry.settings['rdw-visible-fields']` off a linked-entry-shaped value. Both a company
 * envelope on the form (a synced link field) and `UserStore.currentCompanyEntry()` (a plain Entry)
 * carry the same `{ entry: { settings } }` shape. Returns the raw stored value; normalising it is
 * `readRdwVisibleFieldKeys`'s job.
 */
export function rdwVisibleFieldSettingOf(source: unknown): unknown {
  const entry = (source as Record<string, unknown> | undefined)?.['entry'] as Record<string, unknown> | undefined;
  const settings = entry?.['settings'] as Record<string, unknown> | undefined;
  return settings?.['rdw-visible-fields'];
}

/**
 * Normalises a raw `rdw-visible-fields` value into a key array, or null when nothing usable is
 * there: the current shape is a string array; the legacy form-builder toggle shape is an object
 * whose ticked keys hold true or the string 'true' (toggle fields store their value as a string).
 */
export function readRdwVisibleFieldKeys(raw: unknown): string[] | null {
  if (Array.isArray(raw)) {
    return raw.map(String);
  }
  if (raw && typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>)
      .filter(([, value]) => value === true || value === 'true')
      .map(([key]) => key);
  }
  return null;
}

/**
 * Whether a company link value means "no company": unset, null, or the '' a cleared link field
 * writes. `??` alone only covers the first two, so every place that picks or reads the company on
 * the form goes through here.
 */
export function isEmptyCompanyValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Keys the RDW preview may show, or null for "show every mapped field".
 * - No company on the form → the logged-in user's company decides (the carrier branch of the
 *   create form has no company field at all).
 * - A company on the form with a usable setting → that setting.
 * - A company on the form without a usable setting (a stored link holds only `{ id,
 *   selectedInput }`, or a company link field with moduleValueType `id` stores the bare GUID
 *   string): when it is the user's own company (same non-empty id) the user's live setting
 *   decides; when it is another company, null — an admin editing another company's vehicle must
 *   not get the preview filtered by the admin's own company.
 *
 * A usable embedded setting wins even for the user's own company: the module fields' own
 * conditions read that same embed, so the preview stays in step with the form rather than with
 * the settings page; a newer value on the settings page reaches both once the link is synced
 * again.
 */
export function resolveRdwVisibleKeys(formCompany: unknown, userCompany: unknown): string[] | null {
  // A cleared free-text link writes '' rather than null; both mean "no company on the form".
  if (isEmptyCompanyValue(formCompany)) {
    return readRdwVisibleFieldKeys(rdwVisibleFieldSettingOf(userCompany));
  }

  const formKeys = readRdwVisibleFieldKeys(rdwVisibleFieldSettingOf(formCompany));
  if (formKeys !== null) {
    return formKeys;
  }

  const formId = typeof formCompany === 'string' ? formCompany : (formCompany as Record<string, unknown>)['id'];
  const userId = (userCompany as Record<string, unknown> | undefined)?.['id'];
  return formId && userId && formId === userId
    ? readRdwVisibleFieldKeys(rdwVisibleFieldSettingOf(userCompany))
    : null;
}
